import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import parse from 'html-react-parser';

import { Button, Loader, ModalInfo } from '@/components';
import { complianceTransferActions } from '@/redux/actions';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';

import styles from './TransactionAlertsResetAllModal.module.sass';

interface TransactionAlertsResetAllModalProps {
  isOpen: boolean;
  onReset: () => void;
  onClose: () => void;
}

const TransactionAlertsResetAllModal = ({
  isOpen,
  onReset,
  onClose,
}: TransactionAlertsResetAllModalProps) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const [isResetting, setIsResetting] = useState(false);

  const { loading, error } = useAppSelector(
    (state) => state.complianceTransfers,
  );

  const handleOnReset = useCallback(() => {
    setIsResetting(true);
    dispatch(complianceTransferActions.applyDefault());
  }, [dispatch]);

  useEffect(() => {
    if (isResetting && !loading && !error) {
      setIsResetting(false);
      onClose();
      onReset();
    }
  }, [loading, error, isResetting, onClose, onReset]);

  return (
    <ModalInfo
      isOpen={isOpen}
      title={t('translation.alerts_to_default_modal_title')}
      actions={
        <div className={styles.actions}>
          <Button
            size="s"
            type="secondary"
            corner="rect"
            minWidth={77}
            onClick={onClose}
          >
            {t('translation.cancel_btn')}
          </Button>
          <Button
            size="s"
            type="primary"
            corner="rect"
            minWidth={120}
            onClick={handleOnReset}
          >
            {t('translation.reset')}
          </Button>
        </div>
      }
      className={styles.modal}
      onClose={onClose}
    >
      <div className={styles.text}>
        {parse(t('translation.alerts_to_default_modal_description'))}
      </div>

      {loading && (
        <div className={styles.loader}>
          <Loader />
        </div>
      )}
    </ModalInfo>
  );
};

export default TransactionAlertsResetAllModal;
